import { isBefore, isWithinInterval, startOfWeek } from "date-fns";

import { type ControlType, CONTROL_TYPES } from "../types";

/**
 * Gets the localStorage key for tracking spending limit increase requests.
 *
 * `variableCharge` (boost) requests are namespaced so they don't collide with the legacy
 * `total` key; `total`/undefined keep the original key for backward compatibility.
 */
export function getRequestStorageKey(
  workplaceId: string,
  weekStartDate: Date,
  controlType?: ControlType
): string {
  const dateSegment = weekStartDate.toISOString().split("T")[0];
  const typeSegment =
    controlType === CONTROL_TYPES.variableCharge ? `${CONTROL_TYPES.variableCharge}-` : "";
  return `spending-limit-request-${typeSegment}${workplaceId}-${dateSegment}`;
}

/**
 * Gets the Sunday (start of week) for a given date.
 */
export function getWeekStartForDate(date: Date): Date {
  return startOfWeek(date, { weekStartsOn: 0 });
}

/**
 * Saves a spending limit increase request to localStorage.
 * MVP implementation - in the future this could POST to a Slack webhook or internal API.
 */
export function saveSpendingLimitIncreaseRequest(
  workplaceId: string,
  weekStartDate: Date,
  // Display-unit amount requested: hours for the `total` flow, cents for `variableCharge`.
  additionalAmount: number,
  controlType?: ControlType
): void {
  const key = getRequestStorageKey(workplaceId, weekStartDate, controlType);
  const request = {
    requestedAt: new Date().toISOString(),
    additionalAmount,
  };
  try {
    localStorage.setItem(key, JSON.stringify(request));
  } catch {
    // No-op for MVP; localStorage may fail due to quota/security/privacy modes
  }
}

/**
 * Checks if a spending limit increase request has been submitted for a given week.
 * Returns true if a request exists and the spending control hasn't been updated since the request.
 *
 * @param workplaceId - The workplace ID
 * @param weekStartDate - The start date of the week (Sunday)
 * @param spendingControlUpdatedAt - The updatedAt timestamp from the spending control (or createdAt if no updates)
 */
export function hasSpendingLimitIncreaseRequest(
  workplaceId: string,
  weekStartDate: Date,
  spendingControlUpdatedAt: Date | undefined,
  controlType?: ControlType
): boolean {
  const key = getRequestStorageKey(workplaceId, weekStartDate, controlType);

  try {
    const stored = localStorage.getItem(key);

    if (!stored) {
      return false;
    }

    const request = JSON.parse(stored) as { requestedAt: string };
    const requestedAt = new Date(request.requestedAt);

    // If there's no spending control for this week, the request is still pending
    if (!spendingControlUpdatedAt) {
      return true;
    }

    // Request is pending if it was made after the spending control was last updated
    return requestedAt > spendingControlUpdatedAt;
  } catch {
    return false;
  }
}

interface ShiftWithStartDate {
  start: Date;
}

/**
 * Gets all unique week start dates (Sundays) that contain at least one shift.
 * Uses the shift's start date to determine which week it belongs to.
 *
 * @param shifts - Array of shifts with start dates
 * @returns Array of unique week start dates (Sundays)
 */
export function getWeekStartDatesContainingShifts(shifts: ShiftWithStartDate[]): Date[] {
  if (shifts.length === 0) {
    return [];
  }

  // Get min and max shift start dates
  const shiftStartDates = shifts.map((shift) => shift.start);
  const minDate = shiftStartDates.reduce((min, date) => (isBefore(date, min) ? date : min));
  const maxDate = shiftStartDates.reduce((max, date) => (isBefore(max, date) ? date : max));

  // Generate all weeks from min to max
  const weeks: Date[] = [];
  let currentWeekStart = getWeekStartForDate(minDate);
  const lastWeekStart = getWeekStartForDate(maxDate);

  while (!isBefore(lastWeekStart, currentWeekStart)) {
    // Capture current week boundaries for the check
    const weekStart = currentWeekStart;
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);

    const hasShiftInWeek = shifts.some((shift) =>
      isWithinInterval(shift.start, { start: weekStart, end: weekEnd })
    );

    if (hasShiftInWeek) {
      weeks.push(weekStart);
    }

    // Move to next week
    currentWeekStart = new Date(currentWeekStart);
    currentWeekStart.setDate(currentWeekStart.getDate() + 7);
  }

  return weeks;
}
